/**
 * Palette global — Ctrl+K / Cmd+K oriunde în app.
 *
 * Caută pe metadate neсriptate: titluri de notițe + slug-uri, titluri
 * credențiale + username + URL. Nu face search pe conținut criptat
 * (ar necesita decrypt pentru toate — prea scump).
 *
 * Click / Enter pe rezultat → navighează:
 * - notiță: /notes/<tenant_slug>/<path_segments>
 * - credențial: /vault (deschidere modal pentru item e follow-up)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { credentialsApi } from '@/api/credentials';
import { notesApi, type NoteSummary } from '@/api/notes';
import Modal from '@/components/Modal';
import { useAuthStore } from '@/stores/auth';
import { KeyIcon, TypeIcon } from '@/features/notes/icons';

interface Result {
  key: string;
  type: 'note' | 'credential';
  title: string;
  subtitle?: string;
  url: string;
}

export default function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const user = useAuthStore((s) => s.user);
  const currentTenant = user?.tenants.find((m) => m.tenant_id === tenantId);
  const tenantSlug = currentTenant?.slug ?? '';

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const notes = useQuery({
    queryKey: ['notes', tenantId],
    queryFn: () => notesApi.list(tenantId!),
    enabled: !!tenantId && open,
  });
  const credentials = useQuery({
    queryKey: ['credentials', tenantId],
    queryFn: () => credentialsApi.list(tenantId!),
    enabled: !!tenantId && open,
  });

  // Construiește path URL-ul pentru o notiță iterând prin parent-chain.
  const buildNoteUrl = (note: NoteSummary): string => {
    if (!notes.data || !tenantSlug) return '/notes';
    const byId = new Map(notes.data.map((n) => [n.id, n]));
    const segments: string[] = [];
    let current: NoteSummary | undefined = note;
    const seen = new Set<string>();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      segments.unshift(current.slug);
      if (!current.parent_id) break;
      current = byId.get(current.parent_id);
    }
    return `/notes/${tenantSlug}/${segments.join('/')}`;
  };

  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase();
    const out: Result[] = [];
    for (const n of notes.data ?? []) {
      if (
        !q ||
        n.title.toLowerCase().includes(q) ||
        n.slug.toLowerCase().includes(q)
      ) {
        out.push({
          key: `note-${n.id}`,
          type: 'note',
          title: n.title,
          subtitle: n.slug,
          url: buildNoteUrl(n),
        });
      }
    }
    for (const c of credentials.data ?? []) {
      const hay = [c.title, c.username, c.url, ...Object.values(c.meta ?? {}).map(String)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!q || hay.includes(q)) {
        out.push({
          key: `cred-${c.id}`,
          type: 'credential',
          title: c.title,
          subtitle: `${c.type}${c.username ? ' · ' + c.username : ''}`,
          url: `/vault?open=${c.id}`,
        });
      }
    }
    return out.slice(0, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, notes.data, credentials.data]);

  // Reset la deschidere.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const commit = (r: Result) => {
    onClose();
    navigate(r.url);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) =>
        results.length ? (i - 1 + results.length) % results.length : 0,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) commit(results[selectedIndex]);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={null} maxWidth="max-w-xl">
      <div>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('shell_ui.command_palette.placeholder')}
          className="w-full text-base border-0 focus:outline-none focus:ring-0 bg-transparent px-0 pb-3 border-b border-[var(--border-strong)]"
        />
        <div className="mt-3 max-h-96 overflow-y-auto -mx-5 px-2">
          {!tenantId && (
            <p className="px-3 py-4 text-sm text-[var(--text-muted)]">
              {t('shell_ui.command_palette.no_tenant')}
            </p>
          )}
          {results.length === 0 && query.trim() && (
            <p className="px-3 py-4 text-sm text-[var(--text-muted)]">
              {t('shell_ui.command_palette.no_results', { query })}
            </p>
          )}
          {results.map((r, idx) => (
            <button
              key={r.key}
              type="button"
              onClick={() => commit(r)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded text-left transition-colors ${
                idx === selectedIndex
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'hover:bg-[var(--bg-hover)] text-[var(--text)]'
              }`}
              onMouseEnter={() => setSelectedIndex(idx)}
            >
              <span
                className={`w-6 h-6 flex items-center justify-center rounded ${
                  idx === selectedIndex
                    ? 'text-brand-700 dark:text-brand-300'
                    : 'text-[var(--text-muted)]'
                }`}
              >
                {r.type === 'note' ? <TypeIcon /> : <KeyIcon />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{r.title}</span>
                {r.subtitle && (
                  <span className="block text-[11px] text-[var(--text-muted)] truncate">
                    {r.subtitle}
                  </span>
                )}
              </span>
              <span className="text-[10px] uppercase text-[var(--text-dim)]">
                {r.type === 'note'
                  ? t('shell_ui.command_palette.type_note')
                  : t('shell_ui.command_palette.type_credential')}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-[var(--border)] flex justify-between text-[10px] text-[var(--text-dim)]">
          <span>
            <kbd className="font-mono bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded px-1">
              ↑↓
            </kbd>{' '}
            {t('shell_ui.command_palette.hint_navigate')} ·{' '}
            <kbd className="font-mono bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded px-1">
              Enter
            </kbd>{' '}
            {t('shell_ui.command_palette.hint_open')}
          </span>
          <span>
            <kbd className="font-mono bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded px-1">
              Esc
            </kbd>{' '}
            {t('shell_ui.command_palette.hint_close')}
          </span>
        </div>
      </div>
    </Modal>
  );
}
